/**
 * GamificationService - Pure business logic for gamification
 * 
 * This service is:
 * - React-agnostic (no hooks, no context)
 * - DOM-agnostic (no localStorage, no window)
 * - Portable to React Native
 * 
 * All Supabase interactions happen here.
 * React components use this via thin hooks/context wrappers.
 */

import { supabase } from '@/integrations/supabase/client';
import type { MotivationStyle } from '@/contexts/GamificationContext';

export interface GamificationSettings {
  style: MotivationStyle;
  hideRewards: boolean;
  coachmarkSeen: boolean;
  showCelebrations: boolean;
  dailyNudgeEnabled: boolean;
}

class GamificationService {
  /**
   * Load gamification settings for a user
   */
  async loadSettings(userId: string): Promise<GamificationSettings> {
    try {
      const { data } = await supabase
        .from('budget_settings')
        .select('motivation_style, hide_rewards, coachmark_points_seen, show_celebrations, daily_nudge_enabled')
        .eq('user_id', userId)
        .maybeSingle();

      if (data) {
        return {
          style: (data.motivation_style as MotivationStyle) || 'gentle',
          hideRewards: data.hide_rewards || false,
          coachmarkSeen: data.coachmark_points_seen || false,
          showCelebrations: data.show_celebrations || false,
          dailyNudgeEnabled: data.daily_nudge_enabled || false,
        };
      }

      // Default settings
      return {
        style: 'gentle',
        hideRewards: false,
        coachmarkSeen: false,
        showCelebrations: false,
        dailyNudgeEnabled: false,
      };
    } catch (error) {
      console.error('GamificationService.loadSettings error:', error);
      throw error;
    }
  }

  /**
   * Update gamification settings for a user
   */
  async updateSettings(userId: string, updates: Partial<GamificationSettings>): Promise<void> {
    try {
      const dbUpdates: any = {};
      if (updates.style !== undefined) dbUpdates.motivation_style = updates.style;
      if (updates.hideRewards !== undefined) dbUpdates.hide_rewards = updates.hideRewards;
      if (updates.coachmarkSeen !== undefined) dbUpdates.coachmark_points_seen = updates.coachmarkSeen;
      if (updates.showCelebrations !== undefined) dbUpdates.show_celebrations = updates.showCelebrations;
      if (updates.dailyNudgeEnabled !== undefined) dbUpdates.daily_nudge_enabled = updates.dailyNudgeEnabled;

      const { error } = await supabase
        .from('budget_settings')
        .update(dbUpdates)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('GamificationService.updateSettings error:', error);
      throw error;
    }
  }

  /**
   * Format confirmation message based on settings
   */
  formatConfirmation(settings: GamificationSettings, amount?: number): string {
    if (settings.hideRewards || settings.style === 'minimal') {
      return 'Saved.';
    }
    if (settings.style === 'gentle') {
      return 'Logged ✓ — +10 progress';
    }
    // gamified
    return `Logged ✓ — +${amount || 10} points ✨`;
  }

  /**
   * Check if XP indicator should be shown
   */
  shouldShowXPIndicator(settings: GamificationSettings): boolean {
    return !settings.hideRewards && settings.style !== 'minimal';
  }

  /**
   * Get streak emoji based on style
   */
  getStreakEmoji(settings: GamificationSettings): string {
    return settings.style === 'gamified' ? '🔥' : '';
  }

  /**
   * Check if coachmark should be shown
   */
  shouldShowCoachmark(settings: GamificationSettings): boolean {
    return !settings.coachmarkSeen && settings.style !== 'minimal';
  }

  /**
   * Check if celebration should be shown
   */
  shouldShowCelebration(settings: GamificationSettings): boolean {
    return !settings.hideRewards && settings.showCelebrations;
  }
}

// Singleton instance
export const gamificationService = new GamificationService();
